/**
 * React Hooks for Policy Data
 */

import { useCallback, useEffect, useState } from 'react';
import { policyRepository } from '../repositories/PolicyRepository';
import {
    EntityFilterOptions,
    EntitySortOptions,
    Policy
} from '../types/WorkflowEntities';

// Hook for fetching all policies
export function usePolicies(options?: {
    filter?: EntityFilterOptions;
    sort?: EntitySortOptions;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await policyRepository.findAll({
                filter: options?.filter as any,
                sort: options?.sort as any
            });
            setPolicies(data);
        } catch (err) {
            setError(err as Error);
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    }, [options?.filter, options?.sort]);

    useEffect(() => {
        fetchPolicies();

        if (options?.autoRefresh && options?.refreshInterval) {
            const interval = setInterval(fetchPolicies, options.refreshInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchPolicies, options?.autoRefresh, options?.refreshInterval]);

    return {
        policies,
        loading,
        error,
        refetch: fetchPolicies
    };
}

// Hook for fetching a single policy
export function usePolicy(policyId: string | null) {
    const [policy, setPolicy] = useState<Policy | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchPolicy = useCallback(async () => {
        if (!policyId) {
            setPolicy(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await policyRepository.findById(policyId);
            setPolicy(data);
        } catch (err) {
            setError(err as Error);
            setPolicy(null);
        } finally {
            setLoading(false);
        }
    }, [policyId]);

    useEffect(() => {
        fetchPolicy();
    }, [fetchPolicy]);

    return {
        policy,
        loading,
        error,
        refetch: fetchPolicy
    };
}

// Hook for policy CRUD operations
export function usePolicyMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createPolicy = useCallback(async (data: Partial<Policy>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await policyRepository.create(data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePolicy = useCallback(async (id: string, data: Partial<Policy>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await policyRepository.update(id, data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deletePolicy = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await policyRepository.delete(id);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const testPolicy = useCallback(async (policyId: string, testData: any) => {
        setLoading(true);
        setError(null);

        try {
            const result = await policyRepository.testPolicy(policyId, testData);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createPolicy,
        updatePolicy,
        deletePolicy,
        testPolicy,
        loading,
        error
    };
}

// Hook for policy search
export function usePolicySearch(query: string, debounceMs: number = 300) {
    const [results, setResults] = useState<Policy[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        if (!query || query.trim() === '') {
            setResults([]);
        } else {
            const timer = setTimeout(async () => {
                setLoading(true);
                setError(null);

                try {
                    const data = await policyRepository.search(query);
                    setResults(data);
                } catch (err) {
                    setError(err as Error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, debounceMs);
            cleanup = () => clearTimeout(timer);
        }
        return cleanup;
    }, [query, debounceMs]);

    return {
        results,
        loading,
        error
    };
}

// Hook for policies by scope
export function usePoliciesByScope(scope: string) {
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchPolicies = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await policyRepository.findByScope(scope);
                setPolicies(data);
            } catch (err) {
                setError(err as Error);
                setPolicies([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPolicies();
    }, [scope]);

    return {
        policies,
        loading,
        error
    };
}

export default {
    usePolicies,
    usePolicy,
    usePolicyMutations,
    usePolicySearch,
    usePoliciesByScope
};